import { createContext, useContext, useRef, useCallback } from "react";

type BlockerFn = (targetPath: string) => boolean; // returns true if blocked

interface NavigationBlockerContextType {
  registerBlocker: (fn: BlockerFn) => void;
  unregisterBlocker: () => void;
  tryNavigate: (path: string, doNavigate: () => void) => boolean; // returns true if allowed
}

const NavigationBlockerContext = createContext<NavigationBlockerContextType>({
  registerBlocker: () => {},
  unregisterBlocker: () => {},
  tryNavigate: (_, doNavigate) => { doNavigate(); return true; },
});

export const NavigationBlockerProvider = ({ children }: { children: React.ReactNode }) => {
  const blockerRef = useRef<BlockerFn | null>(null);

  const registerBlocker = useCallback((fn: BlockerFn) => {
    blockerRef.current = fn;
  }, []);

  const unregisterBlocker = useCallback(() => {
    blockerRef.current = null;
  }, []);

  const tryNavigate = useCallback((path: string, doNavigate: () => void) => {
    if (blockerRef.current && blockerRef.current(path)) {
      return false; // blocked
    }
    doNavigate();
    return true;
  }, []);

  return (
    <NavigationBlockerContext.Provider value={{ registerBlocker, unregisterBlocker, tryNavigate }}>
      {children}
    </NavigationBlockerContext.Provider>
  );
};

export const useNavigationBlocker = () => useContext(NavigationBlockerContext);
